/**
 * OMYGO saved pieces — the one store behind every heart on the site.
 *
 * Hearts live on the product page, on every product card, and in the header.
 * They all read and write the same localStorage list, so this file owns the
 * list and every control that touches it. It is loaded once, from
 * layout/theme.liquid, and works by delegation: a card rendered into the page
 * later (the saved page fetches its cards) needs no wiring of its own.
 *
 * localStorage only, per device, never synced. The labels say "on this device"
 * for that reason — a control that looks like an account feature and silently
 * is not is worse than one that is honestly local.
 *
 * STORED SHAPE: an array of { id, handle }, oldest first.
 * The first version stored bare product ids, which is enough to paint a heart
 * but not to fetch a card — the storefront has no lookup by id. read() still
 * accepts that legacy shape; such an entry keeps its heart filled and gains its
 * handle the next time any heart for that product is painted.
 *
 * Every heart is rendered `hidden` and revealed here. Without this file a heart
 * would be a button that does nothing, which is worse than no heart.
 */
(function () {
  'use strict';

  var KEY = 'omygo:saved';

  // ---------------------------------------------------------------- store

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      return list
        .map(function (entry) {
          if (entry && typeof entry === 'object') {
            return { id: String(entry.id), handle: entry.handle || null };
          }
          return entry == null ? null : { id: String(entry), handle: null };
        })
        .filter(Boolean);
    } catch (err) {
      // Private mode, disabled storage, or a corrupt value. Saving is a
      // convenience; it must never break the page it sits on.
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (err) {
      /* quota or disabled storage — ignore */
    }
  }

  function indexOf(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return i;
    }
    return -1;
  }

  // ---------------------------------------------------------------- paint

  function paintButton(button, on) {
    button.hidden = false;
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = button.getAttribute(on ? 'data-label-saved' : 'data-label-save');
    if (label) button.setAttribute('aria-label', label);
  }

  // Also the migration path: a legacy entry saved as a bare id picks up its
  // handle here, the first time a heart that knows the handle is painted.
  function paintAll() {
    var list = read();
    var upgraded = false;

    each(document.querySelectorAll('[data-save-button]'), function (button) {
      var id = button.getAttribute('data-product-id');
      if (!id) return;
      var at = indexOf(list, id);
      var handle = button.getAttribute('data-product-handle');
      if (at !== -1 && !list[at].handle && handle) {
        list[at].handle = handle;
        upgraded = true;
      }
      paintButton(button, at !== -1);
    });

    if (upgraded) write(list);
    paintHeader(list.length);
  }

  // The header ships no script of its own. It exposes data-saved-link, the
  // three plural forms of its label and data-saved-count for the numeral — the
  // same contract as the bag count. Pluralisation stays Liquid's job.
  //
  // `animate` is only true when a heart was tapped. Painting on page load sets
  // the number quietly; a beat on every page view would read as news that is
  // not news.
  function paintHeader(count, animate) {
    var link = document.querySelector('[data-saved-link]');
    if (!link) return;
    var key = count === 0 ? 'zero' : count === 1 ? 'one' : 'other';
    var label = link.getAttribute('data-saved-label-' + key);
    if (label) link.setAttribute('aria-label', label.replace('[count]', count));
    link.toggleAttribute('data-has-saved', count > 0);

    var numeral = link.querySelector('[data-saved-count]');
    if (!numeral) return;
    var changed = numeral.textContent.trim() !== String(count);
    numeral.textContent = count;
    numeral.hidden = count === 0;
    if (animate && changed && count > 0) {
      bump(numeral);
      bump(link);
    }
  }

  function bump(el) {
    el.removeAttribute('data-bump');
    // Force a style flush, or removing and re-adding the attribute in the same
    // frame is coalesced into no change and the animation never restarts.
    void el.offsetWidth;
    el.setAttribute('data-bump', '');
    el.addEventListener('animationend', function handler() {
      el.removeAttribute('data-bump');
      el.removeEventListener('animationend', handler);
    });
  }

  // --------------------------------------------------------------- toggle

  document.addEventListener('click', function (event) {
    var target = event.target;
    var button = target && target.closest ? target.closest('[data-save-button]') : null;
    if (!button) return;

    var id = button.getAttribute('data-product-id');
    if (!id) return;

    var list = read();
    var at = indexOf(list, id);
    var on = at === -1;
    if (on) {
      list.push({ id: id, handle: button.getAttribute('data-product-handle') || null });
    } else {
      list.splice(at, 1);
    }
    write(list);

    // Every heart for this piece, not just the one tapped — the product page
    // can show the same piece in its buy column and in a rail.
    each(document.querySelectorAll('[data-save-button][data-product-id="' + id + '"]'), function (b) {
      paintButton(b, on);
    });
    bump(button);
    paintHeader(list.length, true);

    if (!on) removeFromSavedPage(button);
  });

  // Another tab saved or removed something. Repaint, so two open tabs never
  // disagree about the same heart.
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) paintAll();
  });

  // Mobile Safari only fires :active on elements under a touch listener. One
  // passive no-op on the document restores the press state on every heart
  // without a per-button handler.
  document.addEventListener('touchstart', function () {}, { passive: true });

  // ----------------------------------------------------------- saved page

  // The saved page renders its cards by asking Shopify for them one product at
  // a time through the Section Rendering API, using sections/omygo-saved-card.
  // That keeps ONE card template: the grid, the rails and this page all render
  // snippets/omygo-product-card, rather than this file rebuilding it from JSON.
  function initSavedPage() {
    var page = document.querySelector('[data-saved-page]');
    if (!page) return;

    var grid = page.querySelector('[data-saved-grid]');
    var empty = page.querySelector('[data-saved-empty]');
    var root = (page.getAttribute('data-root') || '/').replace(/\/$/, '');
    var section = page.getAttribute('data-section') || 'omygo-saved-card';

    var entries = read().filter(function (entry) {
      return entry.handle;
    });

    if (!entries.length) {
      showEmpty();
      return;
    }

    page.setAttribute('aria-busy', 'true');

    Promise.all(
      entries.map(function (entry) {
        var url = root + '/products/' + encodeURIComponent(entry.handle) + '?section_id=' + section;
        return fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (res) {
            if (res.status === 404) return { id: entry.id, gone: true, html: '' };
            return res.ok ? res.text().then(function (html) {
              return { id: entry.id, html: html };
            }) : { id: entry.id, html: '' };
          })
          .catch(function () {
            return { id: entry.id, html: '' };
          });
      })
    ).then(function (results) {
      var parser = new DOMParser();
      var fragment = document.createDocumentFragment();

      // A 404 means the product was deleted — it will never render again, and
      // leaving it would have the header count pieces this page cannot show.
      // Only a 404: a network failure or an empty body is not proof of
      // anything, and those entries are kept for the next visit.
      var gone = results.filter(function (r) {
        return r.gone;
      }).map(function (r) {
        return r.id;
      });
      if (gone.length) {
        var kept = read().filter(function (entry) {
          return gone.indexOf(entry.id) === -1;
        });
        write(kept);
        paintHeader(kept.length);
      }

      // Newest first: the last thing saved is the likeliest thing wanted.
      results.reverse().forEach(function (r) {
        if (!r.html) return;
        var card = parser.parseFromString(r.html, 'text/html').querySelector('.omy-card');
        if (card) fragment.appendChild(card);
      });

      page.removeAttribute('aria-busy');
      if (!fragment.childNodes.length) {
        showEmpty();
        return;
      }
      grid.appendChild(fragment);
      grid.hidden = false;
      paintAll();
    });

    function showEmpty() {
      page.removeAttribute('aria-busy');
      grid.hidden = true;
      if (empty) empty.hidden = false;
    }
  }

  // Un-saving on the saved page takes the card away rather than leaving an
  // empty heart on a page whose whole point is the saved list.
  function removeFromSavedPage(button) {
    var grid = button.closest('[data-saved-grid]');
    if (!grid) return;
    var card = button.closest('.omy-card');
    if (!card) return;

    card.setAttribute('data-leaving', '');
    var finished = false;
    var done = function () {
      if (finished) return;
      finished = true;
      card.remove();
      if (!grid.querySelector('.omy-card')) {
        grid.hidden = true;
        var empty = document.querySelector('[data-saved-empty]');
        if (empty) empty.hidden = false;
      }
    };
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      done();
    } else {
      card.addEventListener('transitionend', done, { once: true });
      // Belt and braces: a transition that never runs never ends.
      setTimeout(done, 400);
    }
  }

  function each(nodes, fn) {
    Array.prototype.forEach.call(nodes, fn);
  }

  function init() {
    paintAll();
    initSavedPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
